import * as React from "react";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {useTranslation} from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";
import { operatorFromValues, operatorList } from "../state/operatorState";
import {Box} from "@mui/system";
import AgGridCustomTable from "../../../util/component/AgGridCustomTable";
import {GlobalContext} from "../../../util/state/GlobalContextProvider";
import {ColDef, ICellRendererParams} from "ag-grid-community";
import {IconButton, Tooltip} from "@mui/material";
import i18n from "i18next";
import Stack from "@mui/material/Stack";
import {DescriptionRounded, EditRounded} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import { getLocalGridState } from "../../finance/utils/GridUtils";
import { AgGridReact } from "ag-grid-react";
import { FilterDef } from "../../finance/constants/FinanceModels";
import { GridApi, IServerSideGetRowsParams } from "ag-grid-enterprise";
import { IOperatorSearchProps } from "./OperatorSearch";
import axios from "axios";
import { GridReadyEvent } from "ag-grid-community/dist/lib/events";

const OperatorTable = () => {
    const {t} = useTranslation(['operator']);
    const {globalData} = useContext(GlobalContext);
    const formValues = useRecoilValue(operatorFromValues);
    const gridRef = useRef<AgGridReact>(null);
    const defColDefs = [
        {field: 'username', headerName: t('label.username')},
        {field: 'name', headerName: t('label.name')},
        {field: 'surname', headerName: t('label.surname')},
        {field: 'email', headerName: t('label.email')},
        {field: 'profiles', headerName: t('label.profile'),
            cellRenderer: (params: ICellRendererParams) => {
                let result: string[] = [];
                if (params.value) {
                    params.value.forEach((p: any) => {
                        result.push(p.name);
                    });
                } else {
                    result = ['---'];
                }
                return result.length > 1 ? <Tooltip arrow title={result.join(", ")}>
                    <div>{result[0] + ' ...'}</div>
                </Tooltip> : result[0];
            }
        },
        {
            field: "_actions",
            headerName: '',
            sortable: false,
            cellRenderer: (params: any) => {
                return (
                    <Stack direction={"row"} justifyContent={"center"}>
                        <IconButton color="accent"
                                    onClick={() => {
                                        window.open( `${window.location.origin}/operator/list/${params.data.id}`);
                                    }}>
                            <DescriptionRounded/>
                        </IconButton>
                    </Stack>
                );
            }
        }
    ]
    const [columnDefs, setColumnDefs] = useState<ColDef[]>(defColDefs);

    const defaultColDef = useMemo(() => {
        return {
            sortable: true,
            minWidth: 150,
        }
    }, []);

    const findOperators = (filters: any, page: number, size: number, sortList: any[]) => {
        if (filters !== null) {
            let request = {
                "page": page,
                "size": size,
                "sortList": sortList,
                "filter": filters
            }
            return axios.post('/operator/find', request);
        } else {
            return {status: 200, data: {elements: null, totalElements: 0}};
        }
    }

    const serverSideDatasource = useMemo(() => ({
            getRows: async (params: IServerSideGetRowsParams) => {
                const page = params.api.paginationGetCurrentPage() || 0;
                const size = params.api.paginationGetPageSize() || 0;

                let sortList = [];
                params.request.sortModel.length === 0 ? sortList.push({
                    "field": "id",
                    "direction": "asc"
                }) : params.request.sortModel.forEach((s: any) => {
                    sortList.push({
                        "field": s.colId,
                        "direction": s.sort
                    });
                });

                const response = await findOperators(formValues, page, size, sortList);
                if (response?.status === 200) {
                    params.success({
                        rowData: response.data.elements,
                        rowCount: response.data.totalElements
                    });
                    if (response.data.totalElements === 0) {
                        params.api.showNoRowsOverlay();
                    }
                } else {
                    params.fail();
                }
            }
    }), [formValues])

    useEffect(() => {
        let result: any[] = columnDefs;
        if (globalData.user?.legalEntities.length > 1 && !result.find(r => r.field === 'legalEntities')) {
            result = [
                ...result.slice(0, result.length - 1),
                {
                    field: 'legalEntities',
                    headerName: t('label.legalEntity'),
                    cellRenderer: (params: ICellRendererParams) => {
                        let result: string[] = [];
                        if (params.value) {
                            params.value.forEach((legalEntity: any) => {
                                result.push(t('translation:legalEntities.'.concat(legalEntity.name)));
                            });
                        } else {
                            result = ['---'];
                        }
                        return result.length > 1 ? <Tooltip arrow title={result.join(", ")}>
                            <div>{result[0] + ' ...'}</div>
                        </Tooltip> : result[0];
                    }
                },
                ...result.slice(result.length - 1, result.length)
            ]
        }
        if (globalData.user?.dealers.length > 1 && !result.find(r => r.field === 'dealers')) {
            result = [
                ...result.slice(0, result.length - 1),
                {
                    field: 'dealers',
                    headerName: t('label.dealer'),
                    cellRenderer: (params: ICellRendererParams) => {
                        let result: string[] = [];
                        if (params.value) {
                            params.value.forEach((dealer: any) => {
                                result.push(t('translation:dealers.'.concat(dealer.name)));
                            });
                        } else {
                            result = ['---'];
                        }
                        return result.length > 1 ? <Tooltip arrow title={result.join(", ")}>
                            <div>{result[0] + ' ...'}</div>
                        </Tooltip> : result[0];
                    }
                },
                ...result.slice(result.length - 1, result.length)
            ]
        }
        setColumnDefs(result);
    }, [t]);

    return (
        <Box>
            <AgGridCustomTable
                gridRef={gridRef}
                rowModelType={"serverSide"}
                columnDefs={columnDefs}
                gridOptions={
                    {
                        defaultColDef: defaultColDef,
                    }
                }
                serverSideDatasource={serverSideDatasource}
            />
        </Box>
    );
}
export default OperatorTable;
